import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, BookOpen, X } from 'lucide-react';
import {
  TaskyAlert,
  TaskyBadge,
  TaskyButton,
  TaskyEmptyState,
  TaskyInput,
  TaskyLabel,
  TaskySpinner,
  TaskySwitch,
  TaskyTable,
  TaskyTableBody,
  TaskyTableCell,
  TaskyTableHead,
  TaskyTableHeaderCell,
  TaskyTableRow,
  TaskyTextarea,
  useConfirm,
} from '../tasky';
import { useToast } from '../../contexts/ToastContext';
import {
  adminFetchHelpArticles,
  adminFetchHelpArticle,
  adminCreateHelpArticle,
  adminUpdateHelpArticle,
  adminDeleteHelpArticle,
  HelpArticle,
  HelpArticleCreateRequest,
  HelpArticleUpdateRequest,
} from '../../services/api/helpArticleService';

// バックエンドがAIプロンプトに差し込む際の目安（記事単位で丸ごと落とされる上限）。
// バリデーション上のbody上限（100,000字）とは別の、実用上の目安値。
const BODY_LENGTH_HINT_THRESHOLD = 12_000;

interface HelpArticleFormData {
  title: string;
  body: string;
  is_published: boolean;
  sort_order: number;
}

const initialFormData: HelpArticleFormData = {
  title: '',
  body: '',
  is_published: false,
  sort_order: 0,
};

// 管理画面のヘルプ記事編集（FIXES.md F18-b）。NewsManagementSection と同型。
// 本文はプレーンテキスト/軽いMarkdown想定のため textarea（RichTextEditorはHTMLを吐き、
// AIのプロンプトにタグが混ざってしまうため使わない）。
const HelpArticleManagementSection: React.FC = () => {
  const confirm = useConfirm();
  const { showToast } = useToast();
  const [articles, setArticles] = useState<HelpArticle[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState<HelpArticle | null>(null);
  const [formData, setFormData] = useState<HelpArticleFormData>(initialFormData);
  const [isSaving, setIsSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  // 保存後の警告（AdmanGuardに引っかかりうる表記の指摘）。保存はブロックしない。
  const [saveWarnings, setSaveWarnings] = useState<string[]>([]);

  const fetchArticles = useCallback(async (page: number = 1) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await adminFetchHelpArticles(page);
      const articlesList = Array.isArray(response) ? response : (response.data || []);
      const totalLastPage = (response as any).last_page || 1;
      setArticles(articlesList);
      setLastPage(totalLastPage);
    } catch (err) {
      console.error('Failed to fetch help articles:', err);
      setError('ヘルプ記事一覧の読み込みに失敗しました');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchArticles(currentPage);
  }, [fetchArticles, currentPage]);

  const openCreateModal = () => {
    setSelectedArticle(null);
    setFormData(initialFormData);
    setIsModalOpen(true);
  };

  const openEditModal = async (article: HelpArticle) => {
    // まずは一覧の値でモーダルを即座に開く（体感速度優先）。
    setSelectedArticle(article);
    setFormData({
      title: article.title,
      body: article.body,
      is_published: article.is_published,
      sort_order: article.sort_order,
    });
    setSaveWarnings([]);
    setIsModalOpen(true);

    // GET /{id} は warnings も返す（あとから禁止語を足したケースに気づけるよう、
    // 既存記事を開いたときにも警告を出す仕様）。編集フォームを開いた時点で表示する。
    try {
      const detail = await adminFetchHelpArticle(article.id);
      setSelectedArticle(detail.article);
      setFormData({
        title: detail.article.title,
        body: detail.article.body,
        is_published: detail.article.is_published,
        sort_order: detail.article.sort_order,
      });
      setSaveWarnings(detail.warnings);
    } catch (err) {
      // 詳細取得に失敗しても、一覧の値のまま編集は続行できるようにする（編集をブロックしない）。
      console.error('Failed to fetch help article detail:', err);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedArticle(null);
    setFormData(initialFormData);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      showToast('タイトルを入力してください', 'error');
      return;
    }
    if (!formData.body.trim()) {
      showToast('本文を入力してください', 'error');
      return;
    }

    setIsSaving(true);
    try {
      let warnings: string[] = [];
      if (selectedArticle) {
        const updateData: HelpArticleUpdateRequest = {
          title: formData.title,
          body: formData.body,
          is_published: formData.is_published,
          sort_order: formData.sort_order,
        };
        const result = await adminUpdateHelpArticle(selectedArticle.id, updateData);
        warnings = result.warnings;
      } else {
        const createData: HelpArticleCreateRequest = {
          title: formData.title,
          body: formData.body,
          is_published: formData.is_published,
          sort_order: formData.sort_order,
        };
        const result = await adminCreateHelpArticle(createData);
        warnings = result.warnings;
      }
      // 保存は成功させた上で、警告があれば目立つ形で残す（保存自体はブロックしない）。
      setSaveWarnings(warnings);
      closeModal();
      await fetchArticles(currentPage);
    } catch (err) {
      console.error('Failed to save help article:', err);
      showToast('ヘルプ記事の保存に失敗しました', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (article: HelpArticle) => {
    if (!(await confirm({ title: `「${article.title}」を削除してもよろしいですか？`, tone: 'destructive', confirmText: '削除する' }))) return;
    try {
      await adminDeleteHelpArticle(article.id);
      await fetchArticles(currentPage);
    } catch (err) {
      console.error('Failed to delete help article:', err);
      showToast('ヘルプ記事の削除に失敗しました', 'error');
    }
  };

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-base font-semibold text-dark">ヘルプ記事管理</h2>
        <TaskyButton size="sm" onClick={openCreateModal}>
          <Plus />
          新規記事作成
        </TaskyButton>
      </div>

      {/* 保存後の警告（AdmanGuardに引っかかりうる表記があった場合。保存自体はブロックしていない） */}
      {saveWarnings.length > 0 && (
        <TaskyAlert tone="warning" title="⚠️ この記事の一部は回答時に伏せられます" className="mb-4">
          <div className="flex items-start justify-between gap-3">
            <ul className="list-disc pl-4 space-y-1">
              {saveWarnings.map((warning, index) => (
                <li key={index}>{warning}</li>
              ))}
            </ul>
            <button
              type="button"
              onClick={() => setSaveWarnings([])}
              aria-label="警告を閉じる"
              className="shrink-0 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </TaskyAlert>
      )}

      {/* Error message */}
      {error && (
        <TaskyAlert tone="error" className="mb-4">
          {error}
        </TaskyAlert>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center gap-3 py-10 text-sm text-gray-500">
          <TaskySpinner size="md" />
          読み込み中...
        </div>
      )}

      {/* Articles table */}
      {!isLoading && (
        <div>
          {articles.length > 0 ? (
            <>
              <TaskyTable>
                <TaskyTableHead>
                  <TaskyTableRow hoverable={false}>
                    <TaskyTableHeaderCell>ID</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell>タイトル</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell>公開状態</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell>並び順</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell>更新日</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell>操作</TaskyTableHeaderCell>
                  </TaskyTableRow>
                </TaskyTableHead>
                <TaskyTableBody>
                  {articles.map((article) => (
                    <TaskyTableRow key={article.id}>
                      <TaskyTableCell className="text-gray-400">{article.id}</TaskyTableCell>
                      <TaskyTableCell className="font-medium">
                        {article.title.length > 50
                          ? article.title.substring(0, 50) + '...'
                          : article.title}
                      </TaskyTableCell>
                      <TaskyTableCell>
                        <TaskyBadge variant={article.is_published ? 'success' : 'warning'} dot>
                          {article.is_published ? '公開中' : '下書き'}
                        </TaskyBadge>
                      </TaskyTableCell>
                      <TaskyTableCell className="text-gray-500">{article.sort_order}</TaskyTableCell>
                      <TaskyTableCell className="text-gray-500">
                        {new Date(article.updated_at).toLocaleDateString('ja-JP', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                        })}
                      </TaskyTableCell>
                      <TaskyTableCell>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            title="編集"
                            onClick={() => openEditModal(article)}
                            className="w-7 h-7 rounded-full flex items-center justify-center text-gray-400 hover:bg-warm-hover hover:text-gray-600 transition-colors"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            title="削除"
                            onClick={() => handleDelete(article)}
                            className="w-7 h-7 rounded-full flex items-center justify-center text-gray-400 hover:bg-warm-hover hover:text-destructive transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </TaskyTableCell>
                    </TaskyTableRow>
                  ))}
                </TaskyTableBody>
              </TaskyTable>

              {/* Pagination */}
              {lastPage > 1 && (
                <div className="flex items-center justify-center gap-2 mt-6">
                  <button
                    type="button"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 rounded-tasky-sm text-sm bg-white border border-warm-border text-dark hover:bg-warm-hover transition-colors disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed disabled:hover:bg-gray-50"
                  >
                    前へ
                  </button>
                  <span className="px-3 text-sm text-gray-500">
                    {currentPage} / {lastPage}
                  </span>
                  <button
                    type="button"
                    onClick={() => setCurrentPage(prev => Math.min(lastPage, prev + 1))}
                    disabled={currentPage === lastPage}
                    className="px-4 py-2 rounded-tasky-sm text-sm bg-white border border-warm-border text-dark hover:bg-warm-hover transition-colors disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed disabled:hover:bg-gray-50"
                  >
                    次へ
                  </button>
                </div>
              )}
            </>
          ) : (
            <TaskyEmptyState icon={<BookOpen />} title="ヘルプ記事がまだありません" />
          )}
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6">
          <div className="bg-white rounded-tasky-lg w-full max-w-[720px] max-h-[90vh] overflow-auto p-8 shadow-card">
            <h3 className="text-lg font-semibold text-dark mb-6">
              {selectedArticle ? 'ヘルプ記事を編集' : '新規ヘルプ記事作成'}
            </h3>

            <form onSubmit={handleSubmit}>
              {/* Title */}
              <div className="mb-5">
                <TaskyLabel htmlFor="help-article-title" required>タイトル</TaskyLabel>
                <TaskyInput
                  id="help-article-title"
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="記事のタイトルを入力してください"
                  maxLength={255}
                  required
                />
              </div>

              {/* Body */}
              <div className="mb-5">
                <TaskyLabel htmlFor="help-article-body" required>本文</TaskyLabel>
                <p className="text-xs text-gray-400 mb-1.5">
                  プレーンテキストまたは軽いMarkdownで入力してください。この本文はAIの回答に引用されます。
                  APIパス・環境変数名・SQL・AIモデル名などを書くと、回答時にその部分が伏せられます。
                </p>
                <TaskyTextarea
                  id="help-article-body"
                  value={formData.body}
                  onChange={(e) => setFormData(prev => ({ ...prev, body: e.target.value }))}
                  placeholder="記事の本文を入力してください"
                  rows={10}
                  maxLength={100000}
                  required
                />
                <p
                  className={`text-xs mt-1 ${
                    formData.body.length > BODY_LENGTH_HINT_THRESHOLD ? 'text-destructive' : 'text-gray-400'
                  }`}
                >
                  {formData.body.length.toLocaleString()} 文字
                  {formData.body.length > BODY_LENGTH_HINT_THRESHOLD &&
                    `（目安の${BODY_LENGTH_HINT_THRESHOLD.toLocaleString()}字を超えています。上限に達すると記事単位で回答に使われなくなることがあります）`}
                </p>
              </div>

              {/* Sort order */}
              <div className="mb-5">
                <TaskyLabel htmlFor="help-article-sort-order">並び順</TaskyLabel>
                <TaskyInput
                  id="help-article-sort-order"
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => setFormData(prev => ({ ...prev, sort_order: Number(e.target.value) || 0 }))}
                  min={0}
                  max={100000}
                  className="w-32"
                />
                <p className="text-xs text-gray-400 mt-1">小さい値ほど先に扱われます（上限に達すると並び順が後ろの記事から落ちます）</p>
              </div>

              {/* Published toggle */}
              <div className="mb-7">
                <TaskySwitch
                  checked={formData.is_published}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_published: e.target.checked }))}
                  label={
                    <span className="font-semibold text-dark">
                      {formData.is_published ? '公開する' : '下書き保存'}
                    </span>
                  }
                />
                <p className="text-xs text-gray-400 mt-1 ml-[52px]">
                  {formData.is_published
                    ? 'AIの回答時に参照されます'
                    : '下書きとして保存され、AIの回答には使われません'}
                </p>
              </div>

              {/* Form actions */}
              <div className="flex justify-end gap-3">
                <TaskyButton type="button" variant="secondary" onClick={closeModal} disabled={isSaving}>
                  キャンセル
                </TaskyButton>
                <TaskyButton type="submit" isLoading={isSaving}>
                  {isSaving ? '保存中...' : selectedArticle ? '更新する' : '作成する'}
                </TaskyButton>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default HelpArticleManagementSection;
